import { useState } from 'react';
import { Pennant } from 'markcat-ui';
import { useAuthStore } from '../stores/auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { useReveal } from '../hooks/useReveal';
import {
  BillingPeriodToggle,
  PricingCard,
  type PricingCardPlan,
  type PricingCardPrice,
} from '../components/Pricing';
import {
  PAID_PLAN_ORDER,
  PRICING_CATALOG,
  getPriceDisplay,
  type BillingPeriod,
} from '../pricing/catalog';
import styles from './Landing.module.css';

const FEATURES = [
  {
    title: 'Reads the actual page — even behind bot walls and cookie banners',
    desc: 'An escalating fetch pipeline — plain HTTP, then a real browser, then a stealth browser — reads what the page actually says, and detects cookie walls and error pages instead of classifying junk.',
  },
  {
    title: 'Real hierarchical categories, not a flat tag pile',
    desc: 'A three-stage classifier files every bookmark into a nested category tree — Development / Python / Asyncio, not forty loose tags to maintain by hand.',
  },
  {
    title: 'Summaries in your languages',
    desc: 'Titles and summaries in up to three languages of your choice. Save an English article, find and read it later in German.',
  },
  {
    title: 'Find it again in plain words',
    desc: 'Full-text search across titles, URLs, summaries, and translations — the words you would naturally use are in the summary, even when they never appeared on the page.',
  },
  {
    title: 'Save from anywhere',
    desc: 'PWA on desktop and mobile, Chrome extension (Web Store listing coming soon), Android share sheet, iOS capture via Apple Shortcut.',
  },
  {
    title: 'Live across your devices',
    desc: 'Save on your phone, watch it appear classified on your desktop seconds later. No refresh, no sync button.',
  },
];

const FREE_PLAN: PricingCardPlan = {
  name: 'Free',
  description: 'Try it, keep it.',
  features: [
    '25 classifications / month',
    'Up to 500 bookmarks stored',
    'HTTP fetch only',
    '1 summary language',
  ],
};

const SELF_HOSTED_PLAN: PricingCardPlan = {
  name: 'Self-hosted',
  description: 'Run your own server. All features, no limits.',
  features: [
    'Everything in Pro',
    'No classification quota',
    'No storage cap',
    'Your data stays yours',
  ],
};

const FREE_PRICE: PricingCardPrice = {
  amount: '€0',
  period: 'forever',
  annualNote: undefined,
  savingsPercent: null,
};

export function Landing() {
  const [billing, setBilling] = useState<BillingPeriod>('monthly');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  usePageTitle('Markcat — AI bookmark manager that organizes itself');
  const featuresRef = useReveal<HTMLElement>();
  const pricingRef = useReveal<HTMLElement>();
  const ctaRef = useReveal<HTMLElement>();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <h1 className={styles.logo}>
            <Pennant size={28} />
            Markcat
          </h1>
          <nav className={styles.nav}>
            <a href="#features" className={styles.navLink}>Features</a>
            <a href="#pricing" className={styles.navLink}>Pricing</a>
            <a href="/docs/" className={styles.navLink}>Docs</a>
            {isAuthenticated
              ? <a href="/app" className={styles.loginLink}>Open app</a>
              : <a href="/login" className={styles.loginLink}>Login</a>}
          </nav>
        </div>
      </header>

      <main>
      <section className={styles.hero}>
        <h2 className={styles.heading}>Your bookmarks, actually organized.</h2>
        <p className={styles.subtitle}>
          Markcat reads each page — even behind bot walls — files it into a real
          category tree, and finds it again in plain words. No tags to maintain.
        </p>
        <div className={styles.ctaRow}>
          <a href="/login?mode=register" className={styles.cta}>Get started free</a>
          <a href="#pricing" className={styles.ctaSecondary}>See pricing</a>
        </div>
        <div className={styles.heroShotFrame}>
          <img
            src="/landing-hero.png"
            alt="Markcat tiles view with auto-categorized bookmarks"
            width={2400}
            height={1500}
            className={styles.heroShot}
          />
        </div>
      </section>

      <section id="features" ref={featuresRef} className={`${styles.featuresBand} ${styles.reveals}`}>
        <div className={styles.features}>
          <h2 className={`${styles.featuresHeading} ${styles.revealItem}`}>What makes Markcat different</h2>
          <div className={styles.featuresGrid}>
            {FEATURES.map((f, i) => (
              <div
                key={i}
                className={`${styles.featureCard} ${styles.revealItem}`}
                style={{ '--i': i + 1 } as React.CSSProperties}
              >
                <h3 className={styles.featureTitle}>{f.title}</h3>
                <p className={styles.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" ref={pricingRef} className={`${styles.pricing} ${styles.reveals}`}>
        <div className={`${styles.pricingHead} ${styles.revealItem}`}>
          <h2 className={styles.pricingHeading}>Simple pricing</h2>
          <p className={styles.pricingSubtitle}>
            Prices include VAT. Cancel any time. Self-host for free forever.
          </p>
          <BillingPeriodToggle
            value={billing}
            onChange={setBilling}
            className={styles.pricingToggle}
          />
        </div>

        <div className={styles.tiersGrid}>
          <PricingCard
            plan={FREE_PLAN}
            price={FREE_PRICE}
            className={`${styles.landingCard} ${styles.revealItem}`}
            style={{ '--i': 1 } as React.CSSProperties}
            action={<a href="/login?mode=register">Get started</a>}
          />
          {PAID_PLAN_ORDER.map((planKey, index) => {
            const plan = PRICING_CATALOG[planKey];
            const price = getPriceDisplay(plan, billing);
            return (
              <PricingCard
                key={plan.id}
                plan={plan}
                price={price}
                badge={plan.highlight ? 'Most popular' : undefined}
                className={`${styles.landingCard} ${plan.highlight ? styles.landingHighlight : ''}
                  ${styles.revealItem}`}
                style={{ '--i': index + 2 } as React.CSSProperties}
                action={(
                  <a href={`/login?mode=register&plan=${price.key}`}>
                    Subscribe
                  </a>
                )}
              />
            );
          })}
          <PricingCard
            plan={SELF_HOSTED_PLAN}
            price={FREE_PRICE}
            note="Source release in progress — self-hosting opens with the public launch."
            className={`${styles.landingCard} ${styles.revealItem}`}
            style={{ '--i': 4 } as React.CSSProperties}
            action={<a href="/docs/self-hosting/">Self-hosting guide</a>}
          />
        </div>

        <p className={styles.pricingFootnote}>
          &ldquo;Full fetch pipeline&rdquo; means Markcat escalates from plain HTTP to a real
          browser and then a stealth browser when a site blocks simple fetching — the Free plan
          reads pages over plain HTTP only.
        </p>
        <p className={styles.pricingFootnote}>
          One classification credit files one bookmark. Annual credits are granted once at
          signup and never expire — Pro Annual&rsquo;s 10,000 cover a typical full-library
          import on day 1. Top-up packs (€2.99 / €8.99 / €44.99) are available from Billing
          on any paid plan.
        </p>
      </section>

      <section ref={ctaRef} className={`${styles.ctaSection} ${styles.reveals}`}>
        <h2 className={`${styles.ctaHeading} ${styles.revealItem}`}>Ready to organize your bookmarks?</h2>
        <a
          href="/login?mode=register"
          className={`${styles.cta} ${styles.revealItem}`}
          style={{ '--i': 1 } as React.CSSProperties}
        >
          Get started free
        </a>
      </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerRow}>
          <span className={styles.footerBrand}>Markcat</span>
          <nav className={styles.footerNav} aria-label="Footer">
            <a href="/docs/" className={styles.footerLink}>Docs</a>
            <a href="/privacy" className={styles.footerLink}>Privacy</a>
            <a href="/terms" className={styles.footerLink}>Terms</a>
            <a href="/imprint" className={styles.footerLink}>Imprint</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
